import { useCallback, useEffect, useRef, useState } from 'react';

type UseInViewAutoAdvanceOptions = {
  interval?: number;
  threshold?: number;
};

export function useInViewAutoAdvance(
  itemCount: number,
  { interval = 5000, threshold = 0.25 }: UseInViewAutoAdvanceOptions = {},
) {
  const sectionRef = useRef<HTMLElement>(null);
  const [active, setActive] = useState(0);
  const [inView, setInView] = useState(false);
  const [tick, setTick] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const inViewRef = useRef(inView);

  inViewRef.current = inView;

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const advance = useCallback(() => {
    if (itemCount <= 1) return;
    setActive((c) => (c + 1) % itemCount);
  }, [itemCount]);

  const startTimer = useCallback(() => {
    clearTimer();
    if (itemCount <= 1) return;
    timerRef.current = setInterval(advance, interval);
  }, [clearTimer, advance, interval, itemCount]);

  const select = useCallback(
    (index: number) => {
      clearTimer();
      setActive(index);
      setTick((t) => t + 1);
      if (inViewRef.current) startTimer();
    },
    [clearTimer, startTimer],
  );

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setActive(0);
            setTick((t) => t + 1);
            setInView(true);
          } else {
            setInView(false);
            clearTimer();
          }
        });
      },
      { threshold },
    );

    observer.observe(section);
    return () => {
      observer.disconnect();
      clearTimer();
    };
  }, [threshold, clearTimer]);

  useEffect(() => {
    if (inView) {
      startTimer();
    } else {
      clearTimer();
    }
    return clearTimer;
  }, [inView, startTimer, clearTimer]);

  return {
    sectionRef,
    active,
    select,
    inView,
    interval,
    tick,
  };
}
